import * as THREE from 'three/webgpu';
import type { PhysicsWorld, StaticMesh } from '../physics/PhysicsWorld';
import type { RenderOrigin } from '../render/RenderOrigin';
import type { CaveGlow } from './CaveGlow';
import type { CaveMesher } from './CaveMesher';
import type { MeshJob } from './caveJobs';
import type { V3 } from './sdf';
import type { ChunkMesh } from './surfaceNets';

export type InteriorState = 'unloaded' | 'loading' | 'ready';

/**
 * A cave's inside, streamed. `load` has the worker mesh it (nearest the player's gate
 * first); each frame `update` turns a few finished chunks into meshes and colliders,
 * within a time budget, so no frame stalls. The chunks stay cached (a few MB), so coming
 * back rebuilds without meshing again. `unload` drops the meshes and colliders.
 */
export class CaveInterior {
  readonly group = new THREE.Group();
  state: InteriorState = 'unloaded';
  /** Chunks meshed so far, how many there are in all (once known), and how long meshing took (ms). */
  chunksBuilt = 0;
  chunksTotal = 0;
  meshMs = 0;
  private readonly cache: ChunkMesh[] = [];
  private cached = false;
  private next = 0;
  private readonly built: { mesh: THREE.Mesh; body: StaticMesh | null }[] = [];
  private physics: PhysicsWorld | null = null;
  /** Where the chunks' crystals and fungi go (see CaveGlow). */
  glow: CaveGlow | null = null;

  constructor(
    readonly key: string,
    private readonly job: Omit<MeshJob, 'key' | 'from'>,
    /** Universe position of the cave's origin (the layout's). */
    readonly origin: THREE.Vector3,
    private readonly material: THREE.Material,
    private readonly mesher: CaveMesher,
    renderOrigin: RenderOrigin,
  ) {
    this.group.name = `${key} interior`;
    renderOrigin.anchor(this.group, origin);
  }

  setPhysics(physics: PhysicsWorld): void {
    this.physics = physics;
  }

  /** Start loading, meshing outward from `from` (cave-local; the player's gate). */
  load(from: V3): void {
    if (this.state !== 'unloaded') return;
    this.state = 'loading';
    this.next = 0;
    if (this.cached) return;
    this.cache.length = 0;
    this.mesher.mesh(
      { ...this.job, key: this.key, from },
      {
        onChunk: (m) => {
          this.cache.push(m);
        },
        onDone: (chunks, ms) => {
          this.cached = true;
          this.chunksTotal = chunks;
          this.meshMs = ms;
        },
      },
    );
  }

  unload(): void {
    if (this.state === 'unloaded') return;
    if (!this.cached) {
      this.mesher.cancel(this.key);
      this.cache.length = 0;
    }
    for (const b of this.built) {
      this.group.remove(b.mesh);
      b.mesh.geometry.dispose();
      if (b.body) this.physics?.removeStaticMesh(b.body);
    }
    this.built.length = 0;
    this.glow?.clear();
    this.chunksBuilt = 0;
    this.next = 0;
    this.state = 'unloaded';
  }

  /** Build waiting chunks until `budgetMs` is spent. */
  update(budgetMs: number): void {
    if (this.state !== 'loading') return;
    const end = performance.now() + budgetMs;
    while (this.next < this.cache.length && performance.now() < end) this.build(this.cache[this.next++]);
    if (this.cached && this.next >= this.cache.length) this.state = 'ready';
  }

  private build(m: ChunkMesh): void {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(m.positions, 3));
    g.setAttribute('normal', new THREE.BufferAttribute(m.normals, 3));
    g.setAttribute('aLook', new THREE.BufferAttribute(m.looks, 4));
    g.setIndex(new THREE.BufferAttribute(m.indices, 1));
    g.computeBoundingSphere();
    const mesh = new THREE.Mesh(g, this.material);
    this.group.add(mesh);
    const solid = withHulls(m);
    const body = this.physics
      ? this.physics.addStaticMesh(this.origin, solid.positions, solid.indices)
      : null;
    if (m.glow && m.glow.length > 0) this.glow?.add(m.glow);
    this.built.push({ mesh, body });
    this.chunksBuilt++;
  }
}

/** A chunk's walls plus its props' solid hulls, for its collider. */
function withHulls(m: ChunkMesh): { positions: Float32Array; indices: Uint32Array } {
  const hp = m.hullPositions;
  const hi = m.hullIndices;
  if (!hp || !hi || hi.length === 0) return m;
  const positions = new Float32Array(m.positions.length + hp.length);
  positions.set(m.positions);
  positions.set(hp, m.positions.length);
  const indices = new Uint32Array(m.indices.length + hi.length);
  indices.set(m.indices);
  const base = m.positions.length / 3;
  for (let i = 0; i < hi.length; i++) indices[m.indices.length + i] = hi[i] + base;
  return { positions, indices };
}
